import type { ServiceDeployment } from "@microtica/ms-elasticsearch-sdk";

/**
 * The tag of an image reference, which is what ms-kube deploys: `repo:tag`, `host:port/repo:tag` and
 * `repo:tag@sha256:…` give `tag`; a bare tag is returned as is; a reference without a tag gives undefined.
 */
export function imageTag(image?: string): string | undefined {
    if (!image) return undefined;
    const ref = image.split("@")[0];
    const slash = ref.lastIndexOf("/");
    const colon = ref.lastIndexOf(":");
    if (colon > slash) return ref.slice(colon + 1) || undefined;
    return slash < 0 && !image.includes("@") ? ref : undefined;
}

/** The newest deploy of a service in its own namespace: history is per cluster record, and one record can reach several namespaces */
export function latestDeployment<T extends Pick<ServiceDeployment, "namespace">>(history: T[], namespace: string): T | undefined {
    return history.find(d => (d.namespace || "microtica") === namespace);
}

/**
 * The version a redeploy sends to keep the service where it is. The running container says what runs now;
 * the last deploy's `version` is what ms-kube was asked to deploy. Never `commit.version`: that's the image's
 * build label, which images built elsewhere don't have and retagged images don't match, so a settings save
 * would deploy another version.
 */
export function runningVersion(
    appName: string,
    pods: { status: { containerStatuses?: { name: string; image: string }[] } }[],
    last?: Pick<ServiceDeployment, "version">,
): string | undefined {
    const container = pods.flatMap(pod => pod.status.containerStatuses || []).find(c => c.name === appName);
    return imageTag(container?.image) || imageTag(last?.version);
}
